import {
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfMonth,
} from 'date-fns'
import { type WeekStartsOn } from '@opallabs/concepts'

export type CalendarDay = {
  date: Date
  /** `yyyy-MM-dd`, the key every day-indexed map uses. */
  key: string
  isCurrentMonth: boolean
  isToday: boolean
}

export type CalendarWeek = {
  /** Key of the first day in the row. */
  key: string
  days: CalendarDay[]
}

export const DAYS_IN_WEEK = 7

export const makeDayKey = (date: Date): string => format(date, 'yyyy-MM-dd')

const DAY_PRECISION = /^(\d{4}-\d{2}-\d{2})/

/**
 * Reads an item's date as a calendar day in the viewer's local zone.
 *
 * The leading `yyyy-MM-dd` is taken verbatim when it is there, and the rest of
 * the string is thrown away. Which day an item belongs to is settled before it
 * reaches the grid — see `toCalendarItems`, which reads a timed instant in the
 * reader's own zone the way the week view does — so reading a full timestamp
 * again here would only move it a second time.
 *
 * A date that cannot be read is logged and returns `null`. One bad row must not
 * blank the whole grid.
 */
export const parseCalendarDate = (
  value: string | undefined,
  context: { id: string; field?: string }
): Date | null => {
  const dayPrecision = value?.match(DAY_PRECISION)?.[1]
  const parsed = parseISO(dayPrecision ?? value ?? '')

  if (!isValid(parsed)) {
    console.error('Calendar item has an unreadable date, skipping', {
      field: context.field,
      itemId: context.id,
      value,
    })
    return null
  }

  return parsed
}

export type BuildMonthGridParams = {
  /** Any date inside the month to build. */
  month: Date
  today: Date
  weekStartsOn: WeekStartsOn
}

/**
 * Builds the 4–6 week rows a month needs, including the neighbouring-month days
 * that fill out the first and last rows.
 */
export const buildMonthGrid = ({
  month,
  today,
  weekStartsOn,
}: BuildMonthGridParams): CalendarWeek[] =>
  eachWeekOfInterval(
    { start: startOfMonth(month), end: endOfMonth(month) },
    { weekStartsOn }
  ).map((firstDayOfWeek) => ({
    key: makeDayKey(firstDayOfWeek),
    days: eachDayOfInterval({
      start: firstDayOfWeek,
      end: endOfWeek(firstDayOfWeek, { weekStartsOn }),
    }).map((date) => ({
      date,
      key: makeDayKey(date),
      isCurrentMonth: isSameMonth(date, month),
      isToday: isSameDay(date, today),
    })),
  }))

/** Holds a date inside the rendered grid, so arrow keys stop at its edges. */
export const clampDateToGrid = (date: Date, weeks: CalendarWeek[]): Date => {
  const first = weeks[0].days[0].date
  const last = weeks[weeks.length - 1].days[DAYS_IN_WEEK - 1].date

  if (date < first) return first
  if (date > last) return last
  return date
}
